import React from "react";

import { FormattedMessage, injectIntl, IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/Accordion";
import Stereo from "@/icons/__generated__/Stereo/Stereo24";
import { IFaqs } from "~/types/api-types";

import {
  EVENT_FAQ_CONST,
  eventFaqDefinition,
  miscEventFaqDefinitions,
  category_labels,
} from "../data/event-faq-definitions";
import {
  AVOptionsSelected,
  AVRulesSelected,
  AVRulesExplicitlyProhibited,
} from "../data/event-faq-display-sections";

import Description from "./Description";
import { analyticEventOnClick } from "./faq-utils";

interface AVMusicProps {
  faqData: IFaqs;
  intl: IntlShape;
  userID: string;
  listingID: string;
}

interface AVEquipmentOptions {
  microphones: string;
  projector: string;
  speakers: string;
  television: string;
  misc_av_equipment: string;
}

interface AVEquipmentRuleOptions {
  is_dj_allowed: string;
  is_live_music_allowed: string;
  is_amplified_music_allowed: string;
  is_connected_amplified_music_allowed: string;
}
const AVMusic: React.FC<AVMusicProps> = ({
  faqData,
  intl,
  userID,
  listingID,
}) => {
  const { MISC_AV_EQUIPMENT } = EVENT_FAQ_CONST;

  const availableEquipment = AVOptionsSelected(faqData);

  const avRulesSelected = AVRulesSelected(
    faqData,
  ) as (keyof AVEquipmentRuleOptions)[];
  const AVRulesProhibited = AVRulesExplicitlyProhibited(
    faqData,
  ) as (keyof AVEquipmentRuleOptions)[];

  const hasRulesForAV = avRulesSelected.length > 0;

  const hasAvailableEquipment = availableEquipment.length > 0;

  const hasOtherAVEquipment =
    MISC_AV_EQUIPMENT in faqData && faqData["misc_av_equipment"]?.description;

  const label = intl.formatMessage(category_labels.av_music);

  const findAvailableEquipment = (equipment: string) => {
    const equipmentData = faqData[equipment as keyof AVEquipmentOptions];
    return equipmentData ? true : false;
  };

  const findAvailableDescription = (equipment: string) => {
    const equipmentData = faqData[equipment as keyof AVEquipmentOptions];
    return equipmentData ? (equipmentData.description as string) : "";
  };

  const findEquipment = (equipment: string) => {
    return (
      eventFaqDefinition.av_music?.provided_av_equipment[
        equipment as keyof AVEquipmentOptions
      ] ?? {
        id: "",
        defaultMessage: "",
      }
    );
  };

  const findSelectedAvRules = (activity: string) => {
    return (
      eventFaqDefinition.av_music?.av_equipment_rules[
        activity as keyof AVEquipmentRuleOptions
      ] ?? {
        id: "",
        defaultMessage: "",
      }
    );
  };

  const findProhibitedAvRules = (activity: string) => {
    return (
      eventFaqDefinition.av_music?.av_equipment_rules[
        activity as keyof AVEquipmentRuleOptions
      ] ?? {
        id: "",
        defaultMessage: "",
      }
    );
  };

  return (
    <div className="EventFAQ">
      <Accordion
        type="multiple"
        onValueChange={() => {
          analyticEventOnClick(userID, listingID, "AV Music");
        }}
      >
        <AccordionItem value="av-music-section">
          <AccordionTrigger className="tw-py-24">
            <div className="tw-flex tw-gap-12 tw-items-center">
              <Stereo />
              <Text element="h3" typeface="headline-1-m">
                {label}
              </Text>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {hasAvailableEquipment && (
              <div className="tw-space-y-8">
                <Text typeface="body-2-m" element="p">
                  {intl.formatMessage(
                    miscEventFaqDefinitions.available_av_equipment,
                  )}
                </Text>

                {availableEquipment.map((equipment, key) => (
                  <div className="faq-body flex margin-md-bottom" key={key}>
                    <div className="flex-basis-90">
                      <Text element="p">
                        {intl.formatMessage(findEquipment(equipment))}
                      </Text>
                      {findAvailableEquipment(equipment) && (
                        <Text className="tw-text-gray-600 tw-mb-16" element="p">
                          <Description
                            description={findAvailableDescription(equipment)}
                            textSmall
                          />
                        </Text>
                      )}
                    </div>
                  </div>
                ))}
                {hasOtherAVEquipment && (
                  <div>
                    <div>
                      <FormattedMessage
                        {...(eventFaqDefinition.av_music?.provided_av_equipment[
                          "misc_av_equipment"
                        ] ?? { id: "", defaultMessage: "" })}
                      />
                    </div>
                    <Text className="tw-text-gray-600 tw-mb-16" element="p">
                      {faqData["misc_av_equipment"]?.description && (
                        <Description
                          description={faqData["misc_av_equipment"].description}
                        />
                      )}
                    </Text>
                  </div>
                )}
              </div>
            )}
            {hasRulesForAV && (
              <>
                <hr className="tw-border-gray-200 tw-my-16" />
                <div className="tw-space-y-8">
                  <Text typeface="body-2-m" element="p">
                    {intl.formatMessage(miscEventFaqDefinitions.av_rules)}
                  </Text>
                  {avRulesSelected.map((activity, key) => (
                    <Text element="p" key={key}>
                      {intl.formatMessage(findSelectedAvRules(activity))}
                    </Text>
                  ))}
                  {AVRulesProhibited.map(
                    (activity, key) =>
                      faqData[activity] && (
                        <Text
                          className="tw-text-gray-600 tw-line-though"
                          element="p"
                          key={key}
                        >
                          {intl.formatMessage(findProhibitedAvRules(activity))}
                        </Text>
                      ),
                  )}
                </div>
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default injectIntl(AVMusic);
